/* eslint-disable @typescript-eslint/no-unused-vars */
import { AlternateEmail, Edit, Person, Settings } from "@mui/icons-material";
import { Avatar, Box, FormControlLabel, IconButton, Stack, Switch, Typography } from "@mui/material";
import moment from "moment";
import React, { forwardRef, useContext, useEffect, useImperativeHandle, useState } from "react";
import { useDispatch } from "react-redux";
import useFetch from "src/Components/Common/useFetch";
import { userContext } from "src/Components/Contexts/userContext";
import { FrontendActor } from "src/entity/models/FrontendActor";
import { UseCaseType } from "src/entity/recon-entity/ReconInterfaces";
import {
  updateBulkMailedTimeBCB,
  updateIsSendingBulkMailsBCB,
  updateMailSentPartnerCountBCB,
} from "src/slices/partnerCommunication/bulkMailsSlice";
import { API_ENDPOINTS } from "src/Utils/ApiConstants/ApiUrlConstants";
import { BalanceConfirmationBetaStatuses } from "src/Utils/PartnerCommunication";
import SetupAutoReminderAudit from "../../../Common/BulkMail/AutoReminder/SetupAutoReminderAudit";
import BulkMailCheckDialog from "../../../Common/BulkMail/BulkMailCheckDialog";
import EscalateMailDialog from "../../../Common/BulkMail/EscalateMail";
import { UpdateEmailTemplateDialog, UpdateUserEmailSettingDialog } from "../../../Common/CommonDialogs";
import EmailEditorWithTemplate from "../../../Common/EmailEditorWithTemplate";
import {
  CommunicationTypeDetails,
  EmailTemplateDetailsForCommsType,
  VerifyEmailErrorReportDetails,
} from "../../../Common/PartnerCommsInterfaces";
import { PartnerCommsContext } from "../../../Context/PartnerCommunicationContext";
import { BackdropLoader } from "../../../MsmePartnerPortal/PartnerPortal.common";
import { EmailFields } from "../../Common/ViewReplyMailThread";
import BcNewContext from "../BcNewContext";
import MailSchedulerDialog from "./MailSchedulerDialog";

interface SetupMailProps {
  handleBack: () => void;
  handlePageBack: () => void;
  // sendEmailRef: React.MutableRefObject<{ sendEmail: () => Promise<void> }>;
  //   selectedRow: object[] | any[] | Record<string, any>[];
}

type Ref = {
  sendEmail: () => Promise<void>;
};

// eslint-disable-next-line react/display-name
const SetupMail = forwardRef<Ref, SetupMailProps>(({ handleBack, ...props }, ref) => {
  const { actor } = useContext(userContext);
  const { mailingRows, setMailingRows, branchCode, companyId, listUserEmailTemplates, setIntervalBCBeta } =
    useContext(BcNewContext);
  const {
    communicationTypeId,
    emailTemplates,
    emailTemplateContent,
    userEmailSetting,
    taskLabel,
    selectedAutoReminder,
    escalationStarted,
  } = useContext(PartnerCommsContext);
  const dispatch = useDispatch();

  const [files, setFiles] = useState<File[]>([]);

  const [_commsTypeName, setCommsTypeName] = useState("");
  const [emailTemplateDetails, setEmailTemplateDetails] = useState<EmailTemplateDetailsForCommsType>({} as any);

  const [emailSubject, setEmailSubject] = useState(emailTemplateContent?.emailSubject || "");
  const [editorContent, setEditorContent] = useState(emailTemplateContent?.emailBody || "");
  const [emailTemplateId, setEmailTemplateId] = useState(emailTemplateContent?.templateId || null);
  const [templateError, setTemplateError] = useState(false);

  const emailTemplateName = emailTemplates.find((item) => item.templateId === emailTemplateId)?.templateName;

  // update email Template section
  const [openUpdateEmailTemplate, setOpenUpdateEmailTemplate] = useState(false);
  const [openUpdateEmailSettingDlg, setOpenUpdateEmailSettingDlg] = useState(false);

  const firstFoundCommType = mailingRows.find((row) => row.communicationTypeId);
  const allRowsMatched = mailingRows.every(
    (row) => row?.communicationTypeId === firstFoundCommType?.communicationTypeId
  );

  const [openSchedulerDlg, setOpenSchedulerDlg] = useState<boolean>(false);
  const [openBackDrop, setOpenBackDrop] = useState(false);
  const [openBulkMailCheckDlg, setOpenBulkMailCheckDlg] = useState(false);
  const [rowsData, setRowsData] = useState<VerifyEmailErrorReportDetails[]>([]);
  const [reportBase64, setReportBase64] = useState<string | null>(null);

  const GetCommunicationTypeDetail = async (communicationTypeId: number) => {
    return useFetch<{ response: string; data: CommunicationTypeDetails }>(
      API_ENDPOINTS.GET_COMMUNICATION_TYPE_DETAIL.url,
      "GET",
      {
        failureMessage: API_ENDPOINTS.GET_COMMUNICATION_TYPE_DETAIL.failureMessage,
        config: {
          params: {
            communicationTypeId,
          },
        },
        thenCallBack: (_res) => {
          if (_res.data?.data) {
            setCommsTypeName(_res.data.data.name);
            setEmailTemplateDetails(_res.data.data.emailTemplateDetails);
          }
        },
      }
    );
  };

  useEffect(() => {
    GetCommunicationTypeDetail(communicationTypeId);
  }, [communicationTypeId]);

  const handleClose = () => {
    handleBack();
  };

  // Use the ref with useImperativeHandle
  useImperativeHandle(ref, () => ({
    sendEmail: VerifyEmailDetails,
  }));

  const VerifyEmailDetails = async () => {
    setOpenBackDrop(true);

    const businessPartnerDetails = mailingRows.map((row) => ({
      businessPartnerId: row.businessPartnerId,
      mappingId: row.mappingId,
    }));

    const bodyFormData = new FormData();
    bodyFormData.append("businessPartnerDetails", JSON.stringify(businessPartnerDetails));
    bodyFormData.append("emailBody", editorContent);
    bodyFormData.append("emailSubject", emailSubject);
    bodyFormData.append("communicationTypeId", communicationTypeId?.toString());
    bodyFormData.append("taskLabel", taskLabel?.title || undefined);
    bodyFormData.append("isEscalationStarted", escalationStarted?.toString());
    bodyFormData.append("autoReminderTemplateId", selectedAutoReminder?.id?.toString());
    bodyFormData.append("isNewFlow", "true");
    bodyFormData.append("companyId", companyId);
    bodyFormData.append("branchCode", branchCode);

    if (files !== null && files.length > 0) {
      for (const item of files) {
        bodyFormData.append("file", item);
      }
    }

    type VerifyEmailDetailsResponse = {
      errorReport: string;
      errorReportDetailsForUI: VerifyEmailErrorReportDetails[];
      batchDetail: {
        batchNo: number;
        partnerCount: number;
      }[];
    };

    return useFetch<VerifyEmailDetailsResponse>(API_ENDPOINTS.VERIFY_EMAIL_DETAILS.url, "POST", {
      failureMessage: API_ENDPOINTS.VERIFY_EMAIL_DETAILS.failureMessage,
      data: bodyFormData,
      thenCallBack: (res) => {
        if (res.data) {
          const schedulerRowsFinal = res.data.batchDetail.map((row, index) => ({
            batchNo: row.batchNo,
            partnersCount: row.partnerCount,
            mailSendTime: moment()
              .add(index * 2, "hours")
              .toDate(),
          }));
          console.log(schedulerRowsFinal);

          if (res.data.errorReportDetailsForUI.length !== 0 || userEmailSetting.emailCredentialsProvided === false)
            setOpenBulkMailCheckDlg(true);
          else {
            setOpenSchedulerDlg(true);
          }
          // checkIfUserEmailTemplateHasToUpdate();
          setOpenBackDrop(false);
          setRowsData(res.data.errorReportDetailsForUI || []);
          setReportBase64(res.data.errorReport || "");
        }
      },
      catchCallBack: () => {
        setOpenBackDrop(false);
      },
      errorCallback: () => {
        setOpenBackDrop(false);
      },
    });
  };

  const checkIfUserEmailTemplateHasToUpdate = async () => {
    if (emailSubject.trim() === "") {
      setTemplateError(true);
      return;
    }

    const bodyFormData = new FormData();
    bodyFormData.append("emailBody", editorContent);
    bodyFormData.append("emailSubject", emailSubject);
    bodyFormData.append("templateType", UseCaseType.balanceConfirmationBeta);
    bodyFormData.append("templateName", emailTemplateName);

    return new Promise<void>((resolve, reject) => {
      useFetch(API_ENDPOINTS.CHECK_IF_EMAIL_TEMPLATE_NEEDS_TO_BE_ADDED.url, "POST", {
        failureMessage: API_ENDPOINTS.CHECK_IF_EMAIL_TEMPLATE_NEEDS_TO_BE_ADDED.failureMessage,
        data: bodyFormData,
        config: {
          headers: { "Content-Type": "multipart/form-data" },
        },
        thenCallBack: async (response) => {
          // check response here
          if (response.data.isTemplateUpdated) {
            setOpenUpdateEmailTemplate(true);
            resolve();
            // console.log("so why not open");
          } else {
            await sendMailInBulk();
            resolve();
          }
        },
        catchCallBack: reject,
        errorCallback: reject,
      });
    });
  };

  const updateUserEmailTemplate = async () => {
    setOpenUpdateEmailTemplate(false);

    if (emailSubject.trim() === "") {
      setTemplateError(true);
      return;
    }

    const businessPartnerDetails = mailingRows.map((row) => ({
      businessPartnerId: row.businessPartnerId,
      mappingId: row.mappingId,
    }));

    const bodyFormData = new FormData();
    bodyFormData.append("businessPartnerDetails", JSON.stringify(businessPartnerDetails));
    bodyFormData.append("emailBody", editorContent);
    bodyFormData.append("emailSubject", emailSubject);
    bodyFormData.append("templateName", emailTemplateName);
    bodyFormData.append("templateType", UseCaseType.balanceConfirmationBeta);
    bodyFormData.append("senderCompanyId", companyId);

    if (files !== null && files.length > 0) {
      for (const item of files) {
        bodyFormData.append("file", item);
      }
    }

    return new Promise<void>((resolve, reject) => {
      useFetch(API_ENDPOINTS.ADD_USER_EMAIL_TEMPLATE.url, "POST", {
        failureMessage: API_ENDPOINTS.ADD_USER_EMAIL_TEMPLATE.failureMessage,
        showSuccessToast: true,
        data: bodyFormData,
        config: { headers: { "Content-Type": "multipart/form-data" } },
        thenCallBack: async () => {
          await sendMailInBulk();

          // reload templates after Adding/Updating User Email Template
          listUserEmailTemplates();

          resolve();

          // props.setLoader(true);
        },
        catchCallBack: reject,
        errorCallback: reject,
      });
    });
  };

  const sendMailInBulk = async () => {
    const EndPointBC = API_ENDPOINTS.SEND_BULK_EMAIL.url;

    dispatch(updateIsSendingBulkMailsBCB(true));

    return await useFetch(EndPointBC, "POST", {
      failureMessage: API_ENDPOINTS.SEND_BULK_EMAIL.failureMessage,
      showSuccessToast: true,
      data: {
        isNewFlow: true,
        businessPartnerCount: mailingRows.length,
        companyId: companyId,
        branchCode: branchCode,
      },
      thenCallBack: () => {
        // setShowLoadingIcon(false);
        // TODO: add this interval
        setIntervalBCBeta();
        handleClose();

        // update data
        dispatch(updateMailSentPartnerCountBCB(mailingRows.length));
        dispatch(updateBulkMailedTimeBCB(new Date()));

        // go back to main page
        props.handlePageBack();
      },
      catchCallBack: () => {
        // if (props.templateType === "BCB")
        dispatch(updateIsSendingBulkMailsBCB(false));

        handleClose();

        // go back to main page
        props.handlePageBack();
      },
    });
  };

  return (
    <>
      {/* Top */}
      <Box
        className="space_between fade_up_t"
        sx={{
          alignItems: "center",
          padding: "16px 24px",
          bgcolor: "#fff",
          minWidth: "600px",
          zIndex: 1,
          boxShadow:
            "0px 1px 3px 0px rgba(0, 0, 0, 0.06), 0px 1px 1px 0px rgba(0, 0, 0, 0.07), 0px 2px 1px -1px rgba(0, 0, 0, 0.10)",
        }}
      >
        <Stack direction="row" spacing={2} alignItems={"center"}>
          <Box className="center_align_ver_horiz" bgcolor={"#9C27B0"} height={40} width={40} borderRadius={1}>
            <AlternateEmail sx={{ color: "white" }} />
          </Box>

          <Stack>
            <Typography
              variant="h6"
              sx={{
                color: "rgba(0, 0, 0, 0.87)",
              }}
            >
              Setup Email
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "rgba(0, 0, 0, 0.6)",
              }}
            >
              You can select single or bulk partners to send mail
            </Typography>
          </Stack>
        </Stack>

        <IconButton
          size="small"
          onClick={async () => {
            OpenReconSettingsInNewTab(actor);
            setOpenUpdateEmailSettingDlg(true);
          }}
        >
          <Settings />
        </IconButton>
      </Box>

      {/* Botttom */}

      <Stack gap={2} p={3}>
        {/* Main Setup Section */}
        <Box
          component="section"
          className="emailSetupSection"
          sx={{ bgcolor: "#fff", borderRadius: 1, border: "1px solid rgba(0, 0, 0, 0.12)" }}
        >
          <EmailRecipientDetails fromEmail={userEmailSetting.from_email} bpCount={mailingRows.length} />

          <EmailEditorWithTemplate
            inputEditorState={{ emailTemplateId: emailTemplateDetails.id, templateError: templateError }}
            returnEditorState={(emailSubject, editorContent, templateId, files) => {
              setEmailSubject(emailSubject);
              setEditorContent(editorContent);
              setEmailTemplateId(templateId);

              setFiles(files);
            }}
            sectionsCustomizations={{
              subject:
                mailingRows.length > 0 &&
                mailingRows[0].status === BalanceConfirmationBetaStatuses.RequestBalanceConfirmation,
            }}
            otherProps={{
              branchCode: branchCode,
              companyId: companyId,
              listUserEmailTemplates: listUserEmailTemplates,
              templateType: UseCaseType.balanceConfirmationBeta,
            }}
          />
        </Box>
        {!firstFoundCommType && allRowsMatched && <SetupAutoReminderAudit />}
      </Stack>
      {/* Update Email Template Dialog */}
      <UpdateEmailTemplateDialog
        open={openUpdateEmailTemplate}
        setOpen={setOpenUpdateEmailTemplate}
        yesAction={async () => {
          await updateUserEmailTemplate();
        }}
        noAction={async () => {
          await sendMailInBulk();
        }}
      />
      {/* Update User Email Settings Dialog */}
      <UpdateUserEmailSettingDialog open={openUpdateEmailSettingDlg} setOpen={setOpenUpdateEmailSettingDlg} />
      {/* Back Drop Loader for Mail Check Info */}
      <BackdropLoader openBackDrop={openBackDrop} loadingText={"Validating Pre-requisites to send email..."} />
      {/* Bulk Mail Check Dialog */}
      <BulkMailCheckDialog
        open={openBulkMailCheckDlg}
        setOpen={setOpenBulkMailCheckDlg}
        rowsData={rowsData}
        selectedRow={mailingRows}
        reportBase64={reportBase64}
        cancelAction={async () => {
          setOpenBulkMailCheckDlg(false);
          props.handlePageBack();
        }}
        authenticateAction={async () => {
          OpenReconSettingsInNewTab(actor, true);
          setOpenUpdateEmailSettingDlg(true);
        }}
        continueAction={async () => {
          const newArr = mailingRows.filter((row) => {
            return !rowsData.find((errorRow) => errorRow.businessPartnerId === row.businessPartnerId);
          });
          setMailingRows(newArr);
          setOpenSchedulerDlg(true);
        }}
      />
      <MailSchedulerDialog
        open={openSchedulerDlg}
        setOpen={setOpenSchedulerDlg}
        proceedAction={async () => checkIfUserEmailTemplateHasToUpdate()}
      />
    </>
  );
});

export const OpenReconSettingsInNewTab = (actor: FrontendActor, _authDialog?: boolean) => {
  // logic
  const tab = "UserEmailSetting";
  const authDialog = 1;

  const params = _authDialog ? `tab=${tab}&authDialog=${authDialog}` : `tab=${tab}`;

  const uri = `/${actor.name}/reconSettings?${params}`;
  window.open(uri, "_blank")?.focus();
};

const shownActorIds = [1027, 1047, 221747, 224620, 32941, 884232, 877612] as const;

const EmailRecipientDetails = ({ fromEmail, bpCount }) => {
  const { actor } = useContext(userContext);
  const [openUpdateEmailSettingDlg, setOpenUpdateEmailSettingDlg] = useState(false);
  const [openEscMailDlg, setOpenEscMailDlg] = useState(false);
  const { escalationStarted, setEscalationStarted } = useContext(PartnerCommsContext);
  const { mailingRows } = useContext(BcNewContext);

  return (
    <Box
      sx={{
        p: 2,
        bgcolor: "#f9f9f9",
        borderRadius: "4px 4px 0 0",
        borderBottom: 1,
        borderColor: "#dedede",
      }}
    >
      <Stack position={"relative"} gap={2}>
        <Box className="d_flex" gap={2}>
          <EmailFields label="From:" emails={[fromEmail]} isSender />
          <IconButton
            onClick={async () => {
              // logic
              OpenReconSettingsInNewTab(actor, true);
              setOpenUpdateEmailSettingDlg(true);
            }}
          >
            <Edit />
          </IconButton>
          <FormControlLabel
            sx={{ ml: "auto" }}
            checked={escalationStarted}
            control={<Switch checked={escalationStarted} />}
            hidden={
              (mailingRows.length > 0 &&
                mailingRows[0].status === BalanceConfirmationBetaStatuses.RequestBalanceConfirmation) ||
              (!shownActorIds.includes(actor.id as any) && import.meta.env.VITE_APP_NAKAD_ENV !== "test")
            }
            label="Start escalation process for these partners"
            onClick={() => {
              setOpenEscMailDlg(true);
            }}
          />
        </Box>

        {/* To Email Field */}
        <Box className="email_field" sx={{ display: "flex", alignItems: "center" }}>
          <Typography variant="subtitle2" sx={{ color: "#00000061", mr: 2, width: 54 }}>
            To:
          </Typography>

          <Box
            className="d_flex vertical_center_align"
            sx={{
              bgcolor: "background.paper",
              borderRadius: 1,
              p: 0.5,
              flex: 1,
              border: 1,
              borderColor: "#dfdfdf",
              maxWidth: 500,
            }}
          >
            <Stack direction="row" spacing={2} className="vertical_center_align" sx={{ width: "100%" }}>
              <Box className="avatar-group" sx={{ display: "flex", alignItems: "center", mr: 1 }}>
                <AvatarGroup />
              </Box>
              <Typography fontWeight="normal" fontSize={14} color="textPrimary" sx={{ lineHeight: "143%" }}>
                {bpCount} Business Partners
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Stack>
      {/* Update User Email Settings Dialog */}
      <UpdateUserEmailSettingDialog open={openUpdateEmailSettingDlg} setOpen={setOpenUpdateEmailSettingDlg} />
      {/* Escalate Email Dialog */}
      <EscalateMailDialog
        open={openEscMailDlg}
        setOpen={setOpenEscMailDlg}
        selectedRow={mailingRows}
        approveAction={async () => {
          setEscalationStarted(true);
        }}
      />
    </Box>
  );
};

export const AvatarGroup: React.FC = () => {
  const count = 5;
  return (
    <Box display="flex" alignItems="center" gap={-1}>
      {Array<number>(count)
        .fill(0)
        .map((_, index) => (
          <Avatar
            key={index}
            sx={{
              bgcolor: "#bdbdbd",
              border: "2px solid #fff",
              height: 21,
              width: 21,
              ml: index === 0 ? 0 : -2,
              zIndex: count - index,
            }}
          >
            {index < count - 1 ? <Person sx={{ fontSize: 14 }} /> : " "}
          </Avatar>
        ))}
    </Box>
  );
};

export default SetupMail;
